import React, {ReactElement} from "react";

import Dotdotdot from "react-dotdotdot";

import Card, {type CardFlow} from "../../cards/card/Card";
import Button, {type ButtonKind} from "../../buttons/button/Button";
import ModuleBody from "../module-body/ModuleBody";
import Image, {ImageObject} from "../../image/Image";
import {CTA} from "../../buttons/cta-button/CTAButton";
import {ColumnValue} from "../card-layout/CardLayout";
import Dropdown from "../../forms/dropdown/Dropdown";

import translate from "/src/utils/translate";
import {css, generateKeyId} from "/src/utils/utils";

import * as styles from "./lazy-load-card-layout.module.scss";

export interface LazyLoadCardLayoutCard
  extends Omit<
    Queries.FeaturedContentModuleCardFragmentFragment,
    "ctaButtons"
  > {
  ctaButtons: Array<Queries.CtaButtonFragmentFragment> | Array<CTA>;
}

export interface LazyLoadCardLayoutProps {
  buttonText?: string;
  cardClasses?: string;
  cardImageClasses?: string;
  cards: Array<LazyLoadCardLayoutCard>;
  cardFlow?: CardFlow;
  column: ColumnValue;
  copy?: string;
  darkMode?: boolean;
  eyebrow?: string;
  featuredLayout?: boolean;
  filterable?: boolean;
  headline?: string;
  id?: string;
  isImageIcon?: boolean;
  numVisibleRows?: number;
  onButtonClick?: () => unknown;
  openInNewTab?: boolean;
  useCardStyle?: boolean;
  anchorLinkId?: string | null;
}

interface LazyLoadCardLayoutCardFilterItem {
  label: string;
  value: string;
}

/**
 * helper function to get number of cards to be rendered
 * @param {number} row
 * @param {ColumnValue} column
 */
function getLoadMoreStep(row: number, column: ColumnValue): number {
  return row * parseInt(column);
}

const LazyLoadCardLayout = (props: LazyLoadCardLayoutProps): ReactElement => {
  const {
    eyebrow,
    headline,
    copy,
    cards,
    onButtonClick,
    buttonText,
    column = "3",
    darkMode = false,
    numVisibleRows = 1,
    isImageIcon = false,
    openInNewTab = false,
    featuredLayout = false,
    useCardStyle = false,
    filterable = false,
    id = null,
    cardFlow,
    cardImageClasses,
    cardClasses,
    anchorLinkId,
  } = props;

  const [numCardsToShow, setNumCardsToShow] = React.useState(0);
  const columnRef = React.useRef<string>();
  const $cardsContainer = React.useRef<HTMLDivElement | null>(null);
  const mounted = React.useRef<boolean>(false);

  /**
   * This is defined so that we don't get an empty state with no cards shown,
   * which shouldn't be the case since the prop values should already determine
   * what we initially start with. We're not memo-izing this because it's not
   * a complex computation.
   */
  const initialCardsToShow = getLoadMoreStep(numVisibleRows, column);

  const [tagToApply, setTagToApply] = React.useState<string>("");

  const buildFilterOptions = React.useMemo(() => {
    if (filterable === false) return [];

    const optionsMap: Array<LazyLoadCardLayoutCardFilterItem> = cards
      .filter(card => card.tags)
      .reduce((acc: Array<any>, card) => {
        const temp: Array<LazyLoadCardLayoutCardFilterItem> = [];
        (card.tags || [])
          .filter(tag => tag && tag.label)
          .forEach(tag => {
            if ([...acc, ...temp].find(o => o.label === tag?.label)) {
              return null;
            }

            temp.push({label: tag?.label!, value: tag?.label!});
          });
        return [...acc, ...temp.filter(Boolean)];
      }, []);

    return optionsMap;
  }, [cards, filterable]);

  const getFilterResults = () => {
    const filteredCards: Array<Queries.FeaturedContentModuleCardFragmentFragment> =
      [];
    cards.forEach(card => {
      (card.tags || []).forEach(tag => {
        if (tag == null || tag.label == null || tag.label === undefined) {
          return;
        }

        if (
          tag.label === tagToApply &&
          !filteredCards.some(c => c.title === card.title)
        ) {
          filteredCards.push(
            card as Queries.FeaturedContentModuleCardFragmentFragment
          );
        }
      });
    });
    return filteredCards;
  };

  const col = column ? parseInt(column, 10) : 0;

  // featured layout only applicable to > 2 col
  const isFeaturedLayout = featuredLayout === true && col > 2;

  const filteredCards =
    !filterable || Object.keys(tagToApply).length === 0
      ? cards
      : getFilterResults();

  const leadingRowOfCards = [...filteredCards].splice(0, 1);

  const leadingRowOfCard = leadingRowOfCards.length
    ? leadingRowOfCards[0]
    : null;

  const leadingCardContent = leadingRowOfCard ? (
    <>
      <p
        className={css("eyebrow", styles.lazyLoadCardLayout__featured__eyebrow)}
      >
        {leadingRowOfCard.eyebrow}
      </p>
      <h3 className={css("h3", styles.lazyLoadCardLayout__featured__headline)}>
        {leadingRowOfCard.title}
      </h3>
      <p className={css("p", styles.lazyLoadCardLayout__featured__copy)}>
        <Dotdotdot clamp={5} useNativeClamp tagName="span">
          {leadingRowOfCard.copy?.copy}
        </Dotdotdot>
      </p>
    </>
  ) : null;

  // on mount
  React.useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
    }
  });

  React.useEffect(() => {
    if (numCardsToShow === 0 || columnRef.current !== column) {
      const numInitCards = getLoadMoreStep(numVisibleRows, column);
      columnRef.current = column;
      setNumCardsToShow(numInitCards);
    }
  }, [column, numCardsToShow, numVisibleRows]);

  /**
   * Set focus on the first card of the newly loaded row so that screen readers
   * read the content. Some useful reads:
   * - Elements that can receive focus:
   *   https://usability.yale.edu/web-accessibility/articles/focus-keyboard-operability
   * - ARIA live regions, updating content and A11Y:
   *   https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Live_Regions
   *   https://connekthq.com/accessibility-and-ajax-load-more/
   */
  React.useEffect(() => {
    const col = column ? parseInt(column, 10) : 0;
    const focusIndex = numCardsToShow - col;

    // don't focus on card if we haven't loaded any content
    if (numCardsToShow / col === numVisibleRows) {
      return;
    }

    if (focusIndex > 0 && col > 0 && $cardsContainer.current != null) {
      const newCards = [...$cardsContainer.current.children];
      const cardToFocus = newCards[focusIndex] as HTMLElement;

      if (cardToFocus != null) {
        cardToFocus.focus();
      }
    }
  }, [numCardsToShow, column, numVisibleRows]);

  const handleLoadMoreButtonClick = () => {
    if (numCardsToShow < cards.length) {
      const step = getLoadMoreStep(numVisibleRows, column);
      setNumCardsToShow(numCardsToShow + step);

      if (onButtonClick != null) {
        onButtonClick();
      }
    }
  };

  return (
    <section
      className={css(
        styles.lazyLoadCardLayout,
        styles[`lazyLoadCardLayout${column}`],
        darkMode ? styles.lazyLoadCardLayoutDark : ""
      )}
      id={anchorLinkId || id!}
    >
      <ModuleBody
        classes={cardFlow === "row" ? styles.lazyLoadCardLayout__body : ""}
      >
        {(headline != null || copy != null) && (
          <div className={css(styles.lazyLoadCardLayout__copySection)}>
            {eyebrow != null && !isFeaturedLayout && (
              <p
                className={css(
                  "eyebrow",
                  styles.lazyLoadCardLayout__copySection__eyebrow
                )}
              >
                {eyebrow}
              </p>
            )}
            {headline != null && (
              <h2
                className={css(
                  "h2",
                  styles.lazyLoadCardLayout__copySection__headline
                )}
              >
                {headline}
              </h2>
            )}

            {copy != null && (
              <div
                className={css(
                  styles.lazyLoadCardLayout__copySection__copyWrapper
                )}
              >
                <p
                  className={css(
                    "p",
                    styles.lazyLoadCardLayout__copySection__copy
                  )}
                >
                  {copy}
                </p>
              </div>
            )}
          </div>
        )}

        {!!filterable && (
          <div className={styles.lazyLoadCardLayout__filter}>
            <Dropdown
              disableError
              classes={styles.lazyLoadCardLayout__filter__dropdown}
              placeholder={translate("Select a filter")}
              value={tagToApply}
              onChange={setTagToApply}
              label={translate("Filter by:")}
              options={buildFilterOptions}
              name={`lazy-load-card-filter-${id}`}
            />

            <Button
              classes={styles.lazyLoadCardLayout__filter__clearBtn}
              kind="tertiary"
              darkMode={darkMode}
              onClick={() => setTagToApply("")}
            >
              {translate("Clear filter")}
            </Button>
          </div>
        )}

        {/* TODO: add filter section */}
        <div
          role="list"
          className={css(
            styles.lazyLoadCardLayout__cardsList,
            isFeaturedLayout ? styles.lazyLoadCardLayout__cardsListFeatured : ""
          )}
        >
          {filteredCards
            .slice(0, Math.max(numCardsToShow, initialCardsToShow))
            .map((card, i) => {
              let ctaButtons = card.ctaButtons;
              if (!card.ctaButtons && card.ctaLink) {
                ctaButtons = [card.ctaLink];
              }

              const image = card.videoSource
                ? card.videoPreviewImage
                : card.image;

              const key = `column-cards-card-${card.title}-${i}`;

              return (
                <React.Fragment key={generateKeyId(key)}>
                  <div
                    role="listitem"
                    className={css(styles.lazyLoadCardLayout__cardItem)}
                  >
                    {isFeaturedLayout && i === 0 ? (
                      <>
                        <div className={styles.lazyLoadCardLayout__cardInfo}>
                          {leadingCardContent}
                        </div>
                        <Image
                          image={image as ImageObject}
                          classes={styles.lazyLoadCardLayout__cardItem__img}
                        />
                      </>
                    ) : (
                      <div className={styles.lazyLoadCardLayout__cardCol}>
                        <Card
                          kind={card.kind! as ButtonKind}
                          flow={cardFlow}
                          image={image as ImageObject}
                          eyebrow={card.eyebrow!}
                          headline={card.title!}
                          copy={card.copy?.copy!}
                          ctas={
                            ctaButtons as Array<Queries.CtaButtonFragmentFragment>
                          }
                          darkMode={darkMode}
                          videoIconVisible={card.videoIconVisible === true}
                          videoSource={card.videoSource!}
                          portrait={card.portrait === true}
                          showVideoControls={card.videoControls === true}
                          isImageIcon={isImageIcon}
                          openInNewTab={openInNewTab}
                          useCardStyle={useCardStyle}
                          classes={css(
                            styles.lazyLoadCardLayout__card,
                            cardClasses!
                          )}
                          gatsbyImageWrapperClasses={cardImageClasses}
                          logo={card.logo as ImageObject}
                        />
                      </div>
                    )}
                  </div>

                  {isFeaturedLayout && i === 0 && (
                    <div
                      role="listitem"
                      className={css(
                        styles.lazyLoadCardLayout__cardInfo,
                        styles.lazyLoadCardLayout__cardInfoDesktop
                      )}
                    >
                      {leadingCardContent}
                    </div>
                  )}
                </React.Fragment>
              );
            })}
        </div>

        {filteredCards &&
          filteredCards.length > 0 &&
          numCardsToShow !== 0 &&
          numCardsToShow < filteredCards.length && (
            <div className={css(styles.lazyLoadCardLayout__btn)}>
              <Button
                darkMode={darkMode}
                kind="secondary"
                onClick={handleLoadMoreButtonClick}
              >
                {buttonText}
              </Button>
            </div>
          )}

        {/* aria alert, for accessibility support */}
        <div
          role="alert"
          aria-atomic="true"
          className={styles.lazyLoadCardLayout__alert}
          aria-live="assertive"
        >
          {mounted.current
            ? `${translate("There are")} ${numCardsToShow} ${translate(
                "item(s) loaded"
              )}. ${
                numCardsToShow === filteredCards.length
                  ? translate("All items has been loaded")
                  : ""
              }`
            : ""}
        </div>
      </ModuleBody>
    </section>
  );
};

export default LazyLoadCardLayout;
